/**
 * Policies table — POST Product/GetAgentRequestsByAgent
 */
import CryptoJS from "crypto-js";
import {
  GetAgentRequestsByAgent,
  GetAgentRequestCardsAgent,
  AgentRequestDelete,
  RetryPolicyIssue,
  CheckNotificationStatus,
  ResendNotification,
} from "../Source/medicalAgent/index.js";

const CRYPT_KEY = "secret_key";

/**
 * Policy / agent request statuses.
 * Backend ids (SQL): 0 Invoice yaranmayıb, 1 Ödənişi gözləyirik, 2 Aktiv, 3 Ləğv olunub, 4 Qüvvədən düşüb.
 */
export const POLICY_STATUS = {
  NOT_SENT: "not_sent",
  SENT_FOR_PAYMENT: "sent_for_payment",
  PAID: "paid",
  DELETED: "deleted",
  REJECTED: "rejected",
};

export const POLICY_STATUS_ORDER = [
  POLICY_STATUS.NOT_SENT,
  POLICY_STATUS.SENT_FOR_PAYMENT,
  POLICY_STATUS.PAID,
  POLICY_STATUS.DELETED,
  POLICY_STATUS.REJECTED,
];

export const POLICY_STATUS_BY_ID = {
  0: POLICY_STATUS.NOT_SENT,
  1: POLICY_STATUS.SENT_FOR_PAYMENT,
  2: POLICY_STATUS.PAID,
  3: POLICY_STATUS.REJECTED,
  4: POLICY_STATUS.DELETED,
};

export const POLICY_STATUS_ID_BY_KEY = {
  [POLICY_STATUS.NOT_SENT]: 0,
  [POLICY_STATUS.SENT_FOR_PAYMENT]: 1,
  [POLICY_STATUS.PAID]: 2,
  [POLICY_STATUS.DELETED]: 4,
  [POLICY_STATUS.REJECTED]: 3,
};

export const POLICY_STATUS_I18N_KEY = {
  [POLICY_STATUS.NOT_SENT]: "policyStatusNotSent",
  [POLICY_STATUS.SENT_FOR_PAYMENT]: "policyStatusSentForPayment",
  [POLICY_STATUS.PAID]: "policyStatusPaid",
  [POLICY_STATUS.DELETED]: "policyStatusDeleted",
  [POLICY_STATUS.REJECTED]: "policyStatusRejected",
};

export const POLICY_STATUS_CSS_CLASS = {
  [POLICY_STATUS.NOT_SENT]: "policy-status--not-sent",
  [POLICY_STATUS.SENT_FOR_PAYMENT]: "policy-status--sent-for-payment",
  [POLICY_STATUS.PAID]: "policy-status--paid",
  [POLICY_STATUS.DELETED]: "policy-status--deleted",
  [POLICY_STATUS.REJECTED]: "policy-status--rejected",
};

/** Fallback labels when API omits statusName — must match backend CASE mapping. */
const POLICY_STATUS_BACKEND_LABEL = {
  [POLICY_STATUS.NOT_SENT]: "Invoice yaranmayıb",
  [POLICY_STATUS.SENT_FOR_PAYMENT]: "Ödənişi gözləyirik",
  [POLICY_STATUS.PAID]: "Aktiv",
  [POLICY_STATUS.DELETED]: "Qüvvədən düşüb",
  [POLICY_STATUS.REJECTED]: "Ləğv olunub",
};

/** API list field isPolicy: POL = medical policy, PROD = product catalog, else agent request. */
export const IS_POLICY_POL = "POL";
export const IS_POLICY_PROD = "PROD";

export function normalizeIsPolicyKind(raw) {
  if (raw == null || raw === "") return "";
  return String(raw).trim().toUpperCase();
}

/**
 * Detail routing context: URL query ?type=POL|PROD wins over history.state.
 * Example: /policies/10097?type=POL
 */
export function buildDetailListRow(routeId, listRow = null, routeQuery = {}) {
  const id = routeId != null ? String(routeId) : "";
  const kindFromQuery = normalizeIsPolicyKind(routeQuery?.type ?? routeQuery?.isPolicy);

  if (kindFromQuery && id) {
    const merged = {
      ...(listRow && typeof listRow === "object" ? listRow : {}),
      isPolicyKind: kindFromQuery,
      isPolicyRaw: kindFromQuery,
      policyId: id,
    };
    if (kindFromQuery === IS_POLICY_POL) {
      merged.medicalPolicyId = id;
      return merged;
    }
    if (kindFromQuery === IS_POLICY_PROD) {
      merged.productId = id;
      const reqFromQuery =
        routeQuery?.reqId != null && String(routeQuery.reqId).trim() !== ""
          ? String(routeQuery.reqId).trim()
          : "";
      const reqFromRow =
        listRow?.reqId != null && String(listRow.reqId).trim() !== ""
          ? String(listRow.reqId).trim()
          : "";
      merged.reqId = reqFromRow || reqFromQuery;
      return merged;
    }
    merged.reqId = id;
    return merged;
  }

  if (listRow?.isPolicyKind) {
    return { ...listRow, policyId: id || listRow.policyId };
  }
  if (listRow && id) {
    return { ...listRow, policyId: id };
  }
  return listRow;
}

const STATUS_ID_TO_KEY = POLICY_STATUS_BY_ID;

function getSessionUser() {
  try {
    const raw = window.localStorage.getItem("user");
    if (!raw) return null;
    return JSON.parse(CryptoJS.AES.decrypt(raw, CRYPT_KEY).toString(CryptoJS.enc.Utf8));
  } catch {
    return null;
  }
}

export function getSessionAgentId() {
  const user = getSessionUser();
  if (!user || typeof user !== "object") return undefined;
  const raw = user.agentId ?? user.userId ?? user.id;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

function coerceApiBool(value) {
  if (value === undefined || value === null) return null;
  if (value === true || value === 1 || value === "1" || value === "true" || value === "True") {
    return true;
  }
  if (value === false || value === 0 || value === "0" || value === "false" || value === "False") {
    return false;
  }
  return Boolean(value);
}

function pickRowsFromDataNode(raw) {
  if (Array.isArray(raw)) return raw;
  if (!raw || typeof raw !== "object") return [];
  const nested =
    raw.items ??
    raw.Items ??
    raw.list ??
    raw.List ??
    raw.requests ??
    raw.Requests ??
    raw.rows ??
    raw.Rows ??
    raw.data ??
    raw.Data;
  return Array.isArray(nested) ? nested : [];
}

function pickHasAnyFromNode(node) {
  if (!node || typeof node !== "object") return null;
  return coerceApiBool(node.hasAny ?? node.HasAny);
}

/**
 * AgentRequestListResponse — rows in data[], hasAny at response root (swagger).
 * Also supports nested envelope when data is an object wrapper.
 */
function parseAgentRequestsListResponse(response) {
  const body = response?.data;
  if (!body || typeof body !== "object" || body.isSuccess === false) {
    return { rows: [], hasAny: null };
  }

  let hasAny = pickHasAnyFromNode(body);
  const raw = body.data ?? body.Data;

  if (Array.isArray(raw)) {
    return { rows: raw, hasAny };
  }

  if (raw && typeof raw === "object") {
    const nestedHasAny = pickHasAnyFromNode(raw);
    if (nestedHasAny !== null) {
      hasAny = nestedHasAny;
    }
    return { rows: pickRowsFromDataNode(raw), hasAny };
  }

  return { rows: [], hasAny };
}

function formatDateOnly(value) {
  if (value == null || value === "") return "";
  const parsed = parsePolicyDate(value);
  if (parsed) {
    return parsed.toISOString().slice(0, 10);
  }
  const s = String(value).trim();
  return s || "";
}

function parsePolicyDate(value) {
  if (value == null || value === "") return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const s = String(value).trim();
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    const d = new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const az = s.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (az) {
    const d = new Date(Number(az[3]), Number(az[2]) - 1, Number(az[1]));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function parseDaysLeft(value) {
  if (value == null || value === "") return null;
  const n = Number(String(value).trim());
  return Number.isFinite(n) ? Math.max(0, Math.floor(n)) : null;
}

function computeDaysLeftFromEndDate(endDateRaw) {
  const end = parsePolicyDate(endDateRaw);
  if (!end) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  const diff = Math.ceil((end - today) / (24 * 60 * 60 * 1000));
  if (!Number.isFinite(diff)) return null;
  return diff < 0 ? 0 : diff;
}

function resolveDaysLeft(row, endDateRaw) {
  const fromApi = parseDaysLeft(
    pickRowValue(row, "daysLeft", "DaysLeft", "daysLeftStr", "DaysLeftStr")
  );
  if (fromApi != null) return fromApi;
  return computeDaysLeftFromEndDate(endDateRaw);
}

function toApiDateTimeStart(dateStr) {
  const s = String(dateStr || "").trim();
  if (!s) return undefined;
  return s.length <= 10 ? `${s}T00:00:00` : s;
}

function toApiDateTimeEnd(dateStr) {
  const s = String(dateStr || "").trim();
  if (!s) return undefined;
  return s.length <= 10 ? `${s}T23:59:59` : s;
}

function pickApiStatusName(row) {
  const policyStatusName = row?.policyStatusName;
  if (policyStatusName != null && String(policyStatusName).trim() !== "") {
    return String(policyStatusName).trim();
  }
  const statusName = row?.statusName;
  if (statusName != null && String(statusName).trim() !== "") {
    return String(statusName).trim();
  }
  return null;
}

function normalizeStatusSearchText(name) {
  return String(name || "")
    .trim()
    .replace(/\u0130/g, "i") // İ → i (default .toLowerCase() breaks AZ)
    .toLocaleLowerCase("az");
}

export function resolveStatusKeyFromName(name) {
  const lower = normalizeStatusSearchText(name);
  if (!lower) return null;
  if (
    lower.includes("invoice yaranmay") ||
    lower.includes("invoice yanmay") ||
    lower.includes("invoice buraxilmay") ||
    lower.includes("invoice buraxilm") ||
    lower.includes("göndərilmiyib") ||
    lower.includes("gonderilmiyib") ||
    lower.includes("not sent") ||
    lower.includes("yeni") ||
    lower === "new"
  ) {
    return POLICY_STATUS.NOT_SENT;
  }
  if (
    lower.includes("ödənişi gözləyirik") ||
    lower.includes("odenisi gozleyirik") ||
    lower.includes("ödənişə göndərilib") ||
    lower.includes("odenise gonderilib") ||
    lower.includes("sent for payment") ||
    lower.includes("sent to accounting") ||
    lower.includes("waiting for payment")
  ) {
    return POLICY_STATUS.SENT_FOR_PAYMENT;
  }
  if (
    lower.includes("ləğv olunub") ||
    lower.includes("leqv olunub") ||
    lower.includes("imtina edilib") ||
    lower.includes("imtina olunub") ||
    lower.includes("imtina") ||
    lower.includes("cancel") ||
    lower.includes("reject") ||
    lower.includes("отказ") ||
    lower.includes("отмен")
  ) {
    return POLICY_STATUS.REJECTED;
  }
  if (
    lower.includes("qüvvədən düşüb") ||
    lower.includes("quvveden dusub") ||
    lower.includes("silinib") ||
    lower.includes("silinmiş") ||
    lower.includes("silin") ||
    lower.includes("expired") ||
    lower.includes("deleted") ||
    lower.includes("удал") ||
    lower.includes("истёк") ||
    lower.includes("истек")
  ) {
    return POLICY_STATUS.DELETED;
  }
  if (
    lower.includes("aktiv") ||
    lower.includes("active") ||
    (lower.includes("ödənilib") ||
      lower.includes("odenilib") ||
      lower.includes("paid") ||
      lower.includes("оплач")) &&
    !lower.includes("göndər") &&
    !lower.includes("gonder")
  ) {
    return POLICY_STATUS.PAID;
  }
  return null;
}

export function resolveStatusKeyFromId(statusId) {
  const id = Number(statusId);
  if (!Number.isFinite(id)) return null;
  return POLICY_STATUS_BY_ID[id] ?? null;
}

/** Statistics / list rows — resolve by id first, then localized name. */
export function resolveStatusKeyFromStatisticRow(row) {
  if (!row || typeof row !== "object") return null;

  const fromId = resolveStatusKeyFromId(row.statusId ?? row.StatusId);
  if (fromId) return fromId;

  const name = row.statusName ?? row.StatusName;
  const fromName = resolveStatusKeyFromName(name);
  if (fromName) return fromName;

  const nameStr = String(name ?? "").trim();
  if (/^\d+$/.test(nameStr)) {
    return resolveStatusKeyFromId(Number(nameStr));
  }

  const leadingId = nameStr.match(/^(\d+)\s*[-–.:]/);
  if (leadingId) {
    return resolveStatusKeyFromId(Number(leadingId[1]));
  }

  return null;
}

function resolveStatusKey(row) {
  const name = pickApiStatusName(row);
  const fromName = resolveStatusKeyFromName(name);
  if (fromName) return fromName;
  const id = Number(row?.statusId);
  if (Number.isFinite(id) && STATUS_ID_TO_KEY[id] != null) {
    return STATUS_ID_TO_KEY[id];
  }
  return null;
}

export function policyStatusLabelFromId(statusId) {
  const key = STATUS_ID_TO_KEY[Number(statusId)];
  if (!key) return null;
  return POLICY_STATUS_BACKEND_LABEL[key] || null;
}

export function policyStatusCssClassForRow(row) {
  const key = row?.status || resolveStatusKey(row);
  if (!key) return "";
  return POLICY_STATUS_CSS_CLASS[key] || "";
}

function pickRowValue(row, ...keys) {
  for (const key of keys) {
    const v = row[key];
    if (v != null && String(v).trim() !== "") return v;
  }
  return null;
}

/** Extract publicLink token from API row (or from a full print URL path). */
export function pickPublicLink(source) {
  if (!source || typeof source !== "object") return "";
  const raw = pickRowValue(source, "publicLink", "PublicLink", "public_link");
  if (raw == null) return "";
  const s = String(raw).trim();
  if (!s) return "";
  const fromUrl = s.match(/\/print\/\d+\/([^/?#]+)/i);
  return fromUrl ? decodeURIComponent(fromUrl[1]) : s;
}

export function mapAgentRequestListRow(row) {
  if (!row || typeof row !== "object") return null;

  const isPolicyKind = normalizeIsPolicyKind(pickRowValue(row, "isPolicy", "IsPolicy"));
  const entityId = pickRowValue(row, "id", "Id");
  const entityIdStr = entityId != null ? String(entityId) : "";
  const tableReqId =
    pickRowValue(row, "reqId", "ReqId") != null ? String(pickRowValue(row, "reqId", "ReqId")).trim() : "";

  let routeId = "";
  let medicalPolicyId = "";
  let productId = "";
  let reqId = tableReqId;

  if (isPolicyKind === IS_POLICY_POL) {
    medicalPolicyId = entityIdStr;
    routeId = medicalPolicyId;
  } else if (isPolicyKind === IS_POLICY_PROD) {
    productId = entityIdStr;
    routeId = productId;
  } else {
    reqId = tableReqId || entityIdStr;
    routeId = reqId;
  }

  const policyNumberRaw = pickRowValue(row, "policyNumber", "PolicyNumber");
  const policyNumber = policyNumberRaw != null ? String(policyNumberRaw).trim() : "";
  const policyDate = formatDateOnly(
    pickRowValue(row, "policyDate", "PolicyDate") ?? row.createDate ?? row.CreateDate
  );
  const startDate = formatDateOnly(
    pickRowValue(row, "startDate", "StartDate") ??
      row.startDateStr ??
      row.StartDateStr ??
      row.policyDate ??
      row.PolicyDate ??
      row.createdDate ??
      row.CreatedDate
  );
  const endDateRaw =
    pickRowValue(row, "endDate", "EndDate") ?? row.endDateStr ?? row.EndDateStr ?? null;
  const endDate = formatDateOnly(endDateRaw) || (endDateRaw != null ? String(endDateRaw).trim() : "");
  const daysLeft = resolveDaysLeft(row, endDateRaw);
  const statusIdRaw = pickRowValue(row, "statusId", "StatusId");

  const statusKey = resolveStatusKey({
    ...row,
    statusId: statusIdRaw ?? row.statusId ?? row.StatusId,
  });

  return {
    isPolicyKind,
    isPolicyRaw:
      pickRowValue(row, "isPolicy", "IsPolicy") != null
        ? String(pickRowValue(row, "isPolicy", "IsPolicy"))
        : "",
    reqId,
    medicalPolicyId,
    productId,
    policyId: routeId,
    policyNumber: policyNumber || "—",
    insuredClient:
      (pickRowValue(row, "insuredName", "InsuredName") != null
        ? String(pickRowValue(row, "insuredName", "InsuredName")).trim()
        : "") || "—",
    productName:
      (pickRowValue(row, "prodName", "ProdName") != null
        ? String(pickRowValue(row, "prodName", "ProdName")).trim()
        : "") || "—",
    status: statusKey,
    statusId: statusIdRaw ?? row.statusId ?? row.StatusId ?? null,
    statusName: pickApiStatusName(row) || policyStatusLabelFromId(statusIdRaw),
    statusDisplay: pickApiStatusName(row) || policyStatusLabelFromId(statusIdRaw),
    policyDate,
    startDate,
    endDate,
    daysLeft,
    invoiceNumber:
      pickRowValue(row, "invoiceNumber", "InvoiceNumber") != null
        ? String(pickRowValue(row, "invoiceNumber", "InvoiceNumber")).trim()
        : "",
    invoiceLink:
      pickRowValue(row, "invoiceLink", "InvoiceLink", "invoiceUrl", "InvoiceUrl") != null
        ? String(
            pickRowValue(row, "invoiceLink", "InvoiceLink", "invoiceUrl", "InvoiceUrl")
          ).trim()
        : "",
    publicLink: pickPublicLink(row),
  };
}

/** Policies whose contract end date is within this many days (inclusive). */
export const POLICIES_EXPIRING_SOON_DAYS = 7;

/**
 * @param {{
 *   policyNumber?: string,
 *   insuredClient?: string,
 *   status?: string,
 *   dateFrom?: string,
 *   dateTo?: string,
 *   maxDaysLeft?: number | string,
 *   searchByDaysLeft?: boolean,
 *   searchByStandard?: boolean,
 * } | null} filters
 */
export function buildGetAgentRequestsByAgentPayload(filters = null) {
  const payload = {
    requestCorrelationId: crypto.randomUUID(),
  };
  const agentId = getSessionAgentId();
  if (agentId != null) {
    payload.agentId = agentId;
  }
  if (!filters || typeof filters !== "object") {
    payload.searchByDaysLeft = false;
    payload.searchByStandard = true;
    return payload;
  }
  if (filters.searchByDaysLeft) {
    const maxDaysLeftRaw = filters.maxDaysLeft;
    const maxDaysLeft = Math.floor(Number(maxDaysLeftRaw));
    payload.searchByDaysLeft = true;
    payload.searchByStandard = false;
    payload.maxDaysLeft =
      Number.isFinite(maxDaysLeft) && maxDaysLeft >= 0
        ? maxDaysLeft
        : POLICIES_EXPIRING_SOON_DAYS;
    return payload;
  }

  payload.searchByDaysLeft = false;
  payload.searchByStandard = true;

  const policyNumber = String(filters.policyNumber || "").trim();
  if (policyNumber) payload.policyNumber = policyNumber;
  const insuredName = String(filters.insuredClient || filters.insuredName || "").trim();
  if (insuredName) payload.insuredName = insuredName;
  if (filters.status) {
    const statusId = POLICY_STATUS_ID_BY_KEY[filters.status];
    if (statusId != null) payload.statusId = statusId;
  } else if (filters.statusId != null && filters.statusId !== "") {
    payload.statusId = Number(filters.statusId);
  }
  const dateFrom = String(filters.dateFrom || "").trim();
  const dateTo = String(filters.dateTo || "").trim();
  const apiDateFrom = toApiDateTimeStart(dateFrom);
  const apiDateTo = toApiDateTimeEnd(dateTo);
  if (apiDateFrom) payload.dateFrom = apiDateFrom;
  if (apiDateTo) payload.dateTo = apiDateTo;
  return payload;
}

export function isPolicyExpiringWithinDays(row, maxDays = POLICIES_EXPIRING_SOON_DAYS) {
  const left = row?.daysLeft;
  if (left == null || left === "") return false;
  const n = Number(left);
  return Number.isFinite(n) && n >= 0 && n <= maxDays;
}

export async function loadAgentRequestsByAgent(filters = null) {
  const response = await GetAgentRequestsByAgent(buildGetAgentRequestsByAgentPayload(filters));
  const { rows, hasAny } = parseAgentRequestsListResponse(response);
  const policies = rows.map(mapAgentRequestListRow).filter(Boolean);
  return { policies, hasAny };
}

function formatDateTimeShort(value) {
  if (value == null || value === "") return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toISOString().slice(0, 10);
}

function cardCell(value) {
  if (value == null || String(value).trim() === "") return "—";
  return String(value).trim();
}

function pickRowField(row, ...keys) {
  if (!row || typeof row !== "object") return undefined;
  for (const key of keys) {
    const v = row[key];
    if (v != null && String(v).trim() !== "") return v;
  }
  return undefined;
}

/** GetAgentRequestCardsAgent returns table fields; GetAgentRequestCards — raw person rows. */
export function mapAgentRequestCardToTableRow(row) {
  if (!row || typeof row !== "object") return null;

  const card = {
    cardFullNumber: pickRowField(row, "cardFullNumber", "CardFullNumber"),
    insuredName: pickRowField(row, "insuredName", "InsuredName"),
    groupName: pickRowField(row, "groupName", "GroupName"),
    cardStatusName: pickRowField(row, "cardStatusName", "CardStatusName"),
    sexStr: pickRowField(row, "sexStr", "SexStr", "sex", "Sex"),
    startDateStr: pickRowField(row, "startDateStr", "StartDateStr"),
    endDateStr: pickRowField(row, "endDateStr", "EndDateStr"),
    dobStr: pickRowField(row, "dobStr", "DobStr"),
    lastName: pickRowField(row, "lastName", "LastName"),
    firstName: pickRowField(row, "firstName", "FirstName"),
    middleName: pickRowField(row, "middleName", "MiddleName"),
    pin: pickRowField(row, "pin", "Pin"),
    insuredId: pickRowField(row, "insuredId", "InsuredId"),
    dob: pickRowField(row, "dob", "Dob"),
  };

  const hasAgentCardShape =
    card.insuredName != null ||
    card.groupName != null ||
    card.cardStatusName != null ||
    card.startDateStr != null ||
    card.endDateStr != null ||
    card.dobStr != null;

  if (hasAgentCardShape) {
    return {
      cardFullNumber: cardCell(card.cardFullNumber),
      insuredName: cardCell(card.insuredName),
      groupName: cardCell(card.groupName),
      cardStatusName: cardCell(card.cardStatusName),
      sexStr: cardCell(card.sexStr),
      startDateStr: cardCell(card.startDateStr),
      endDateStr: cardCell(card.endDateStr),
      dobStr: cardCell(card.dobStr) !== "—" ? cardCell(card.dobStr) : formatDateTimeShort(card.dob),
    };
  }

  const name = [card.lastName, card.firstName, card.middleName]
    .map((s) => (s != null ? String(s).trim() : ""))
    .filter(Boolean)
    .join(" ");

  return {
    cardFullNumber:
      card.pin != null
        ? String(card.pin)
        : card.insuredId != null
          ? String(card.insuredId)
          : "—",
    insuredName: name || cardCell(card.insuredName),
    groupName: cardCell(card.groupName),
    cardStatusName: cardCell(card.cardStatusName),
    sexStr: cardCell(card.sexStr),
    startDateStr: cardCell(card.startDateStr),
    endDateStr: cardCell(card.endDateStr),
    dobStr: cardCell(card.dobStr) !== "—" ? cardCell(card.dobStr) : formatDateTimeShort(card.dob),
  };
}

/** Detail view for agent request (reqId), not medical PolicyById. */
export async function loadAgentRequestDetail(reqId, listRow = null) {
  const idNum = Number(reqId);
  if (!Number.isFinite(idNum)) {
    throw new Error("Invalid request id");
  }

  const response = await GetAgentRequestCardsAgent(
    {
      requestCorrelationId: crypto.randomUUID(),
      reqId: idNum,
    }
  );

  const raw = response?.data?.data ?? response?.data?.Data;
  const cardsRaw = raw == null ? [] : Array.isArray(raw) ? raw : [raw];
  const insuredCards = cardsRaw.map(mapAgentRequestCardToTableRow).filter(Boolean);

  const policyNumber =
    listRow?.policyNumber && listRow.policyNumber !== "—"
      ? listRow.policyNumber
      : `REQ-${idNum}`;

  return {
    policyId: String(reqId),
    reqId: String(reqId),
    detailMode: "agentRequest",
    medicalPolicy: {
      policyId: idNum,
      policyFullNumber: policyNumber,
      insured: {
        name: listRow?.insuredClient && listRow.insuredClient !== "—" ? listRow.insuredClient : "",
        phone: "",
        address: "",
      },
      beneficiary: {},
      startDateStr: "",
      endDateStr: listRow?.endDate || "",
    },
    productName: listRow?.productName || "",
    statusDisplay: listRow?.statusDisplay || "",
    publicLink: pickPublicLink(listRow),
    adminCharge: null,
    limitPremium: null,
    sellerLogin: null,
    insuredCards,
    exceptions: [],
    programs: [],
    groups: [],
  };
}

export async function deleteAgentRequest(reqId) {
  const idNum = Number(reqId);
  if (!Number.isFinite(idNum)) {
    throw new Error("Invalid request id");
  }

  return AgentRequestDelete({
    requestCorrelationId: crypto.randomUUID(),
    reqId: idNum,
  });
}

function buildPolicyActionPayload({ reqId, policyId } = {}) {
  const payload = { requestCorrelationId: crypto.randomUUID() };
  const reqNum = Number(reqId);
  if (Number.isFinite(reqNum) && reqNum > 0) payload.reqId = reqNum;
  const policyNum = Number(policyId);
  if (Number.isFinite(policyNum) && policyNum > 0) payload.policyId = policyNum;
  return payload;
}

/** Status: Invoice buraxılmayıb / yaranmayıb — invoice not issued yet. */
export function isInvoiceNotIssuedStatus(row) {
  if (!row || typeof row !== "object") return false;
  if (resolveStatusKey(row) === POLICY_STATUS.NOT_SENT) return true;
  const name = pickApiStatusName(row) || row.statusDisplay || row.statusName || "";
  const lower = normalizeStatusSearchText(name);
  return (
    lower.includes("invoice buraxilmay") ||
    lower.includes("invoice buraxilm") ||
    lower.includes("invoice yaranmay") ||
    lower.includes("invoice yanmay")
  );
}

/** After invoice issued — check whether notification was delivered. */
export function shouldCheckNotificationStatus(row) {
  if (!row || typeof row !== "object") return false;
  if (isInvoiceNotIssuedStatus(row)) return false;
  return resolveStatusKey(row) === POLICY_STATUS.SENT_FOR_PAYMENT;
}

export function parseNotificationStatusResponse(response) {
  const body = response?.data;
  if (!body || body.isSuccess === false) {
    return { checked: true, sent: false, error: true };
  }
  const data = body.data != null ? body.data : body;
  const sentRaw =
    data?.isSent ??
    data?.notificationSent ??
    data?.sent ??
    data?.isNotificationSent ??
    data?.messageSent;
  if (sentRaw === true || sentRaw === 1 || String(sentRaw).toLowerCase() === "true") {
    return { checked: true, sent: true, error: false };
  }
  if (sentRaw === false || sentRaw === 0 || String(sentRaw).toLowerCase() === "false") {
    return { checked: true, sent: false, error: false };
  }
  const statusText = normalizeStatusSearchText(
    data?.status ?? data?.notificationStatus ?? data?.messageStatus ?? ""
  );
  if (statusText.includes("sent") || statusText.includes("gonderilib") || statusText.includes("göndərilib")) {
    return { checked: true, sent: true, error: false };
  }
  if (
    statusText.includes("fail") ||
    statusText.includes("error") ||
    statusText.includes("not sent") ||
    statusText.includes("gonderilm") ||
    statusText.includes("göndərilm")
  ) {
    return { checked: true, sent: false, error: false };
  }
  return { checked: true, sent: false, error: false };
}

export async function retryPolicyIssueAction({ reqId, policyId } = {}) {
  const payload = buildPolicyActionPayload({ reqId, policyId });
  if (payload.reqId == null && payload.policyId == null) {
    throw new Error("Invalid policy action id");
  }
  return RetryPolicyIssue(payload);
}

export async function checkNotificationStatusAction({ reqId, policyId } = {}) {
  const payload = buildPolicyActionPayload({ reqId, policyId });
  if (payload.reqId == null && payload.policyId == null) {
    throw new Error("Invalid policy action id");
  }
  return CheckNotificationStatus(payload);
}

export async function resendNotificationAction({ reqId, policyId } = {}) {
  const payload = buildPolicyActionPayload({ reqId, policyId });
  if (payload.reqId == null && payload.policyId == null) {
    throw new Error("Invalid policy action id");
  }
  return ResendNotification(payload);
}
